import {
  CameraIntegration,
  contactSupportMessage,
  formatTitle,
  epochTimeOfSelectedEvent,
} from './base';
import axios from 'axios';
import { post } from '../../helpers/securityService';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

export class Lemon extends CameraIntegration {
  constructor(...args) {
    super(...args);
    this.cameraProviderName = 'Lemon';
  }

  checkForCredentials() {
    // This Lemon code mixes authToken and accessToken so it is easy to mix up.   When the grid is first loaded,
    // the accessToken is null and the authToken is pulled from redis.  So check to see if authToken is present and valid
    // and if so, set it to camera.accessToken for the rest of the code to use.  If not, the user will be prompted to enter credentials.
    const authToken = this.camera?.authToken;
    this.isAuthenticated = ((this.camera?.authorized && this.camera?.authorized === true) &&
     (authToken && authToken !== 'refresh_token_invalid' && authToken !== 'refresh_token_not_available'));

    if (this.isAuthenticated) {
      this.camera.accessToken = authToken;
    }
  }

  async submitCredentials(username, password) {
    const { location } = this.camera;

    const loginCredentials = {
      un: username,
      pw: password,
    };

    const response = await post(
      `security/lemon_submit_creds/${location}`,
      loginCredentials,
      { orgId: this.orgId },
    ).catch((err) => {
      console.error(err);
      throw Error(
        'We were unable to authenticate your AI Vision Technologies account. Please try again later.',
      );
    });

    this.camera.accessToken = response.authToken;
    this.camera.cameraList = response.cameraList;
    this.camera.authorized = true;

    this.isAuthenticated = true;
  }

  async fetchThumbnails() {
    const { cameraList, apiHost, accessToken } = this.camera;

    const thumbnailPromises = cameraList.map(async (camera) => {
      const url = `${apiHost}/cameras/thumbnails/${camera.id}`;

      const headers = {
        Authorization: accessToken,
      };

      try {
        const response = await axios.get(url, {
          headers,
          responseType: 'blob',
        });
        const responseData = response.data;
        const title = formatTitle(camera.name);
        const imageBlob = new Blob([responseData]);
        const imageUrl = URL.createObjectURL(imageBlob);
        return { src: imageUrl, title, id: camera.id };
      } catch (error) {
        console.error(error);
        throw new Error(
          'We were unable to fetch the video thumbnails for AI Vision Technologies at this time. Please try again later. If this error is recurring, please contact support for assistance.',
        );
      }
    });

    const thumbnails = await Promise.all(thumbnailPromises);

    return thumbnails;
  }

  async fetchVideo(cameraId, seekTime) {
    const { timezone, commonSettings, cameraList, accessToken, apiHost } =
      this.camera;

    const { time } = this.event;
    const nowPlaying = {
      seekButtons: true,
      offsetSeconds: 0,
      displayTime: dayjs(time).tz(timezone).format('YYYY-MM-DD HH:mm:ss'),
    };

    // If video seek button pressed
    if (seekTime) {
      nowPlaying.offsetSeconds += seekTime;
      nowPlaying.displayTime = dayjs(
        nowPlaying.displayTime,
        'YYYY-MM-DD HH:mm:ss',
      )
        .add(seekTime, 'second')
        .format('YYYY-MM-DD HH:mm:ss');
      this.event.time = nowPlaying.displayTime;
    }

    if (!cameraList || !cameraList[0]) {
      console.error('Missing cameraList:', cameraList);
      throw Error(
        `We were unable to find the cameras associated with your AI Vision Technologies account. ${contactSupportMessage}`,
      );
    }

    if (cameraId)
      nowPlaying.camera = cameraList.find((cam) => cam.id === cameraId);
    else [nowPlaying.camera] = cameraList;

    if (Object.keys(nowPlaying.camera).length === 0) {
      console.error(
        'Could not find the camera associated with the provided camera id in the camera list',
        { cameraId, cameraList },
      );
      throw Error(
        `We were unable to find the video for this camera because this camera was not found to be associated with your AI Vision Technologies account. ${contactSupportMessage}`,
      );
    }

    const videoDuration = commonSettings?.videoDurationSeconds || 30;
    const videoEndTime = dayjs(nowPlaying.displayTime, 'YYYY-MM-DD HH:mm:ss')
      .add(videoDuration, 'seconds')
      .format('HH:mm:ss');

    nowPlaying.event = this.event;
    nowPlaying.description = `${nowPlaying.camera.name} on ${nowPlaying.displayTime} to ${videoEndTime}`;
    nowPlaying.cameraId = nowPlaying.camera.id;

    if (!nowPlaying.cameraId) {
      console.error(
        'Could not find the camera id from the selected camera needed to update the video player',
        nowPlaying.camera,
      );
      throw Error(
        `We were unable to identify the selected camera in order to display the associated video. ${contactSupportMessage}`,
      );
    }

    const epochMicroSeconds = epochTimeOfSelectedEvent({
      format: 'micro',
      time: nowPlaying.displayTime,
      timezone,
    });

    const timeString = dayjs(epochMicroSeconds / 1000)
      .tz(timezone)
      .add(14, 'seconds')
      .format('YYYYMMDDHHmmss'); // For lemon, we need to add fourteen seconds

    const src = `${apiHost}/events/${nowPlaying.cameraId}/${timeString}?token=${accessToken}`;

    nowPlaying.video = {
      sources: [{ src, type: 'video/mp4' }],
      ...this.commonVideoOptions,
    };

    return nowPlaying;
  }
}
